/**
 * InlineDateCell — admin/team can click to edit due_date (and optional range end).
 * Non-staff see a plain label. For range items (due_date_end present) we render
 * "Day X to Y" when the engagement is active, otherwise two <input type=date>.
 */
import React, { useState, useRef, useEffect } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Pencil, Check, X, CalendarRange } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" }); } catch { return iso; }
}

export default function InlineDateCell({
  engagementId, kind /* "deliverable"|"meeting" */, itemId,
  startDate, endDate, canEdit, onSaved,
}) {
  const [editing, setEditing] = useState(false);
  const [s, setS] = useState(startDate || "");
  const [e, setE] = useState(endDate || "");
  const [busy, setBusy] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => { setS(startDate || ""); setE(endDate || ""); }, [startDate, endDate]);

  useEffect(() => {
    if (!editing) return;
    const onDoc = (ev) => {
      if (rootRef.current && !rootRef.current.contains(ev.target)) setEditing(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [editing]);

  const save = async () => {
    setBusy(true);
    try {
      const path = kind === "meeting" ? "meetings" : "deliverables";
      await api.put(`/engagements/${engagementId}/${path}/${itemId}/date`, {
        due_date: s || "",
        due_date_end: e || "",
      });
      onSaved && onSaved();
      setEditing(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update date");
    } finally { setBusy(false); }
  };

  if (!canEdit) {
    return (
      <span className="text-[11px] font-mono text-zinc-400 whitespace-nowrap" data-testid={`date-cell-${itemId}`}>
        {endDate
          ? <>{fmtDate(startDate)} <span className="text-zinc-600">to</span> {fmtDate(endDate)}</>
          : fmtDate(startDate)}
      </span>
    );
  }

  if (!editing) {
    return (
      <button type="button" onClick={() => setEditing(true)}
        data-testid={`date-cell-${itemId}`}
        className="inline-flex items-center gap-1 text-[11px] font-mono text-zinc-300 hover:text-white hover:bg-zinc-900 px-1.5 py-0.5 rounded transition-colors group whitespace-nowrap">
        {endDate && <CalendarRange className="w-2.5 h-2.5 text-zinc-500" />}
        {endDate
          ? <>{fmtDate(startDate)} <span className="text-zinc-600">to</span> {fmtDate(endDate)}</>
          : fmtDate(startDate)}
        <Pencil className="w-2.5 h-2.5 opacity-0 group-hover:opacity-100 text-zinc-500 ml-0.5" />
      </button>
    );
  }

  return (
    <div ref={rootRef} className="inline-flex items-center gap-1 bg-[#0a0a0a] border border-zinc-700 rounded px-1 py-0.5"
      data-testid={`date-editor-${itemId}`}>
      <input type="date" value={s} onChange={(ev) => setS(ev.target.value)}
        className="bg-transparent border-0 text-[11px] text-zinc-200 font-mono outline-none w-28"
        data-testid={`date-editor-start-${itemId}`} />
      <span className="text-zinc-600 text-[10px]">to</span>
      <input type="date" value={e} onChange={(ev) => setE(ev.target.value)}
        placeholder="(opt)"
        className="bg-transparent border-0 text-[11px] text-zinc-200 font-mono outline-none w-28"
        data-testid={`date-editor-end-${itemId}`} />
      <button onClick={save} disabled={busy}
        className="w-5 h-5 rounded bg-emerald-800 hover:bg-emerald-700 text-white flex items-center justify-center"
        data-testid={`date-editor-save-${itemId}`}>
        <Check className="w-2.5 h-2.5" strokeWidth={4} />
      </button>
      <button onClick={() => setEditing(false)}
        className="w-5 h-5 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 flex items-center justify-center"
        data-testid={`date-editor-cancel-${itemId}`}>
        <X className="w-2.5 h-2.5" strokeWidth={4} />
      </button>
    </div>
  );
}
